import { useState, useCallback } from 'react';
import { useScheduleChangeLog } from '@/hooks/useScheduleChangeLog';
import { formatDateFull } from '@/lib/scheduleUtils';

interface Props {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
}

function changeTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    item_date_changed: 'Date Changed',
    item_completed: 'Item Completed',
    linked_items_recalculated: 'Linked Items Recalculated',
    initial_timeline_generated: 'Initial Timeline Generated',
    critical_path_recalculated: 'Critical Path Recalculated',
    baseline_set: 'Baseline Set',
  };
  return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

function changeTypeColor(type: string): string {
  const colors: Record<string, string> = {
    item_date_changed: '#E8621A',
    item_completed: '#059669',
    linked_items_recalculated: '#0891B2',
    initial_timeline_generated: '#6366F1',
    critical_path_recalculated: '#EF4444',
    baseline_set: '#8B5CF6',
  };
  return colors[type] || '#6B7280';
}

export default function ScheduleHistoryDrawer({ projectId, isOpen, onClose }: Props) {
  const { logs, loading } = useScheduleChangeLog(projectId);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpand = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div className="relative z-10 w-[420px] max-w-full bg-white shadow-2xl h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#F9FAFB] flex items-center justify-center">
              <i className="ri-history-line text-[#374151] text-sm"></i>
            </div>
            <h2 className="text-sm font-bold text-[#1A1A1A]">Schedule History</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center cursor-pointer transition-colors"
            aria-label="Close history panel"
          >
            <i className="ri-close-line text-[#6B7280]"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line animate-spin text-[#E8621A] text-xl"></i>
              <span className="ml-2 text-sm text-[#9CA3AF]">Loading history...</span>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 rounded-xl bg-[#F9FAFB] flex items-center justify-center mx-auto mb-3">
                <i className="ri-history-line text-[#D1D5DB] text-xl"></i>
              </div>
              <p className="text-sm text-[#9CA3AF] font-medium">No schedule history yet</p>
              <p className="text-xs text-[#D1D5DB] mt-1">
                History will appear when schedule items are edited or recalculated.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => {
                const isExpanded = expandedId === log.id;
                const color = changeTypeColor(log.change_type);
                const dateStr = log.created_at
                  ? new Date(log.created_at).toLocaleDateString(undefined, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : '';
                const timeStr = log.created_at
                  ? new Date(log.created_at).toLocaleTimeString(undefined, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '';

                return (
                  <div
                    key={log.id}
                    className="rounded-xl border border-gray-100 bg-white overflow-hidden transition-all hover:border-gray-200"
                  >
                    {/* Summary row */}
                    <button
                      onClick={() => toggleExpand(log.id)}
                      className="w-full text-left px-4 py-3 flex items-start gap-3 cursor-pointer"
                    >
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5"
                        style={{ backgroundColor: color }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider"
                            style={{ backgroundColor: `${color}12`, color }}
                          >
                            {changeTypeLabel(log.change_type)}
                          </span>
                          {log.affected_item_count > 0 && (
                            <span className="text-[10px] text-[#9CA3AF] bg-gray-50 px-1.5 py-0.5 rounded">
                              {log.affected_item_count} affected
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[#1A1A1A] font-medium mt-1 truncate">
                          {log.schedule_item_title || 'Project-wide change'}
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[10px] text-[#9CA3AF]">
                            {dateStr} at {timeStr}
                          </span>
                          {log.changed_by_role && (
                            <span className="text-[10px] text-[#9CA3AF]">
                              {log.changed_by_role}
                            </span>
                          )}
                        </div>
                      </div>
                      <i
                        className={`ri-arrow-down-s-line text-[#9CA3AF] text-xs transition-transform flex-shrink-0 mt-1 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="px-4 pb-3 pt-0 border-t border-gray-50">
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {log.old_start_date && log.new_start_date && (
                            <div>
                              <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider">Start</p>
                              <p className="text-[11px] text-[#374151]">
                                <span className="text-[#9CA3AF] line-through">
                                  {formatDateFull(log.old_start_date)}
                                </span>
                                <i className="ri-arrow-right-line text-[#9CA3AF] mx-1 text-[9px]"></i>
                                <span className="text-[#1A1A1A] font-medium">
                                  {formatDateFull(log.new_start_date)}
                                </span>
                              </p>
                            </div>
                          )}
                          {log.old_end_date && log.new_end_date && (
                            <div>
                              <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider">End</p>
                              <p className="text-[11px] text-[#374151]">
                                <span className="text-[#9CA3AF] line-through">
                                  {formatDateFull(log.old_end_date)}
                                </span>
                                <i className="ri-arrow-right-line text-[#9CA3AF] mx-1 text-[9px]"></i>
                                <span className="text-[#1A1A1A] font-medium">
                                  {formatDateFull(log.new_end_date)}
                                </span>
                              </p>
                            </div>
                          )}
                          {log.old_duration_days !== null && log.new_duration_days !== null && (
                            <div>
                              <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider">Duration</p>
                              <p className="text-[11px] text-[#374151]">
                                <span className="text-[#9CA3AF] line-through">
                                  {log.old_duration_days}d
                                </span>
                                <i className="ri-arrow-right-line text-[#9CA3AF] mx-1 text-[9px]"></i>
                                <span className="text-[#1A1A1A] font-medium">
                                  {log.new_duration_days}d
                                </span>
                              </p>
                            </div>
                          )}
                          {log.change_reason && (
                            <div className="col-span-2">
                              <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider">Reason</p>
                              <p className="text-[11px] text-[#374151]">{log.change_reason}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}